import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@global/hooks/useAuth';
import { useAppDispatch, useAppSelector } from '@global/store/hooks';
import { getCartData } from '@global/store/slices/cart/extraReducers/getCartData';
import { setCartId } from '@global/store/slices/cart/cartSlice';
import { cartSelector } from '@global/store/slices/cart/selectors';

// CSS and assets
import '@pages/orderConfirmation/style.scss';

// Components
import { ButtonCTA } from '@global/components/UI Elements/ButtonCTA';
import { Header, OrderInfo } from '@pages/orderConfirmation/components';
import { Spinner } from '@global/components/UI Elements/Spinner';
import { OrderItem } from '@global/components/OrderItem';
import { OrderPrice } from '@global/components/OrderPrice';
import { AccountInfo } from './components/AccountInfo';
import { PrintReceipt } from './components/PrintReceipt';
import { AddressInfo } from './components/AddressInfo';
import { BillingInfo } from './components/BillingInfo';
import { EntitlementButton } from './components/EntitlementButton';

// Utils
import analytic from '@global/utils/AdobeAnalytics';
import { waitForAdobeAnalytics } from '@global/utils/waitForAdobeAnalytics';
import { getSearchParamFromUrl } from '@global/utils/url';

// Types
import { LOCALE_CODES } from '@global/types/types';

import { CART_UID_URL_PARAM_KEY } from '@global/App/constants/constants';

import { isB2BAvailableProduct } from './helpers';
import { SiteConfig } from '@src/global/utils/SiteConfig';
import { useEntitlementPolling } from '@global/hooks/useEntitlementPolling';
import {
  getSiteEntitlementConfig,
  getMyAccountUrl,
  shouldShowMyAccountButton,
} from '@global/config/siteEntitlementConfig';

export const OrderConfirmation: React.FC = () => {
  const { i18n, t } = useTranslation();
  const language = i18n.language as LOCALE_CODES;

  const dispatch = useAppDispatch();
  const cartState = useAppSelector(cartSelector);
  const { user, isLoggedIn } = useAuth();

  const { siteId, isSubscriptionSite } = SiteConfig.getConfig();
  const siteConfig = getSiteEntitlementConfig(siteId);

  const productSku = cartState.orderItems?.[0]?.maId;

  // Get entitlement validation params from site configuration
  const entitlementPollingResult = useEntitlementPolling({
    productSku: siteConfig.validationType === 'exactSku' ? productSku : null,
    sitePrefix: siteConfig.sitePrefix || null,
    enabled: siteConfig.shouldCheckEntitlement,
  });
  const hasEntitlementData = entitlementPollingResult.entitlements.length > 0;
  const entitlementData = {
    loading: entitlementPollingResult.loading,
    error: entitlementPollingResult.error,
    showButton:
      entitlementPollingResult.hasActiveEntitlement || hasEntitlementData,
  };

  const selectedDeliveryOption = cartState.deliveryOptions?.find(
    ({ id }) => id === cartState.wSelectedDeliveryOption?.wdoCode
  );

  useEffect(() => {
    const orderUid = getSearchParamFromUrl(CART_UID_URL_PARAM_KEY);
    dispatch(setCartId(orderUid));
    dispatch(getCartData({ language }));
  }, []);

  const handleContinueShopping = () => {
    const continueShoppingUrl = SiteConfig.buildContinueShoppingUrl();
    analytic.dataLayerContinueShoppingClick(
      continueShoppingButtonLabel,
      continueShoppingUrl
    );
    window.open(continueShoppingUrl, '_blank');
  };

  const handleMyAccount = () => {
    const url = getMyAccountUrl(siteId, language);
    // Use navigation for internal links, window.open for external
    if (siteConfig.myAccountRedirect === 'subscriptionDetails') {
      window.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  };

  const handleEntitlementButtonClick = () => {
    if (siteConfig.entitlementButton) {
      const url = siteConfig.entitlementButton.getButtonUrl(language);
      window.open(url, '_blank');
    }
  };

  const entitlementButtonConfig = siteConfig.entitlementButton
    ? {
        buttonLabel: siteConfig.entitlementButton.buttonLabel,
        onButtonClick: handleEntitlementButtonClick,
        titleTranslationKeyPrefix:
          siteConfig.entitlementButton.titleTranslationKeyPrefix,
        supportButtonLabel: siteConfig.entitlementButton.supportButtonLabel,
        showActionButton: siteConfig.entitlementButton.showActionButton,
      }
    : null;

  const isBulkOrder = isB2BAvailableProduct(cartState.orderItems);
  const continueShoppingButtonLabel = t(
    `confirmation.leaveConfirmationPage.${siteConfig.continueShoppingLabel}`
  );

  useEffect(() => {
    if (cartState.orderItems.length && cartState.biId && cartState.orderPrice) {
      waitForAdobeAnalytics(() => {
        analytic.dataLayerOrderConfirmation({
          orderItems: cartState.orderItems,
          orderId: cartState.biId,
          orderPrice: cartState.orderPrice,
          promotions: cartState.promotions,
          isLoggedIn: !cartState.isGuestOrder && isLoggedIn,
          loginId: cartState.isGuestOrder
            ? cartState.shippingInfo.email
            : user.email,
          paymentType: cartState.paymentInfo?.wPaymentType,
          deliveryOption: cartState?.deliveryOptions.find(
            ({ id }) => id === cartState?.wSelectedDeliveryOption?.wdoCode
          ),
        });
      });
    }
  }, [
    cartState.biId,
    cartState.orderPrice,
    cartState.orderItems,
    cartState.promotions,
  ]);

  return (
    <div className="order-confirmation base-container">
      <Header
        email={
          cartState.isGuestOrder ? cartState.shippingInfo.email : user.email
        }
        isBulkOrder={isBulkOrder}
      />
      {cartState.isLoading && <Spinner size="64px" withOverlay />}
      {!cartState.isLoading && (
        <>
          <div className="order-confirmation--section">
            {entitlementButtonConfig ? (
              <EntitlementButton
                {...entitlementData}
                {...entitlementButtonConfig}
              />
            ) : (
              <AccountInfo
                isSubscription={cartState.isSubscription}
                isGuestOrder={cartState.isGuestOrder}
                isBulkOrder={isBulkOrder}
              />
            )}

            <PrintReceipt />
          </div>

          <div className="order-confirmation--section">
            <OrderInfo
              orderNumber={
                isSubscriptionSite ? cartState.lastBillBiId : cartState.biId
              }
              placementDate={cartState.orPlacementDate}
            />
          </div>

          {cartState.isSubscription && (
            <div className="order-confirmation--section">
              <BillingInfo
                orderPrice={cartState.orderPrice}
                nextBillDate={cartState.nextBillDate}
              />
            </div>
          )}

          <div className="order-confirmation--section">
            <AddressInfo
              billingInfo={cartState.billingInfo}
              shippingInfo={cartState.shippingInfo}
              isShowShippingInfo={cartState.isPhysical}
              paymentInfo={cartState.paymentInfo}
            />
          </div>

          <div className="order-confirmation--section product-list">
            {cartState.orderItems?.map((item, index) => {
              const discount = cartState.discounts.find(
                (d) => d.itemNumber === item.biiId
              );
              return (
                <OrderItem
                  isSubscription={cartState.isSubscription}
                  key={item.maName + index}
                  orderItem={item}
                  discount={discount}
                />
              );
            })}
          </div>

          <OrderPrice
            deliveryOption={selectedDeliveryOption}
            isFreeTrial={cartState.isFreeTrial}
            orderPrice={cartState.orderPrice}
            isSubscription={cartState.isSubscription}
          />

          {shouldShowMyAccountButton(siteId) && entitlementData.showButton && (
            <ButtonCTA
              className="order-confirmation__continue-shopping--btn"
              variant="secondary"
              onClick={handleMyAccount}
            >
              {t('confirmation.accountInfo.myAccount')}
            </ButtonCTA>
          )}

          {!shouldShowMyAccountButton(siteId) && (
            <ButtonCTA
              className="order-confirmation__continue-shopping--btn"
              variant="secondary"
              onClick={handleContinueShopping}
            >
              {continueShoppingButtonLabel}
            </ButtonCTA>
          )}
        </>
      )}
    </div>
  );
};
